
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevRadio } from '@ds/components/Radio'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconSearch } from '@ds/icons/IconSearch'
import isEmpty from 'lodash/isEmpty'

import translations from './QuestionsForm.translations'

export const QUESTIONS_FORM_ID = 'buybackQuestionsForm'

const INFOBLOCK_ICONS = {
  search: IconSearch,
  battery: IconBattery,
  shield: IconQuality,
  info: IconInfo,
}

const QUESTION_TYPES = {
  SELECT: 'select',
  RADIO: 'radioline',
  CHECKBOX: 'checkbox',
}

const DEFAULTS_LOOKUP = {
  [QUESTION_TYPES.RADIO]: null,
  [QUESTION_TYPES.CHECKBOX]: [],
  [QUESTION_TYPES.SELECT]: '',
}

export default {
  components: {
    RevButton,
    RevRadio,
    RevInputSelect,
    RevStickyBar,
    RevInfoBlock,
  },
  props: {
    funnel: {
      type: Array,
      default: () => [],
    },
    activeStep: {
      type: Object,
      default: () => ({}),
    },
    hasSubmitButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {},
      activeQuestions: [],
    }
  },
  computed: {
    QUESTIONS_FORM_ID: () => QUESTIONS_FORM_ID,
    IconInfo: () => IconInfo,
    translations: () => translations,
    isStepComplete() {
      return this.activeStep.completed
    },
    isLastQuestion() {
      return this.activeQuestions.some(
        (question) => question.lastQuestion === true,
      )
    },
    buttonLabel() {
      return this.isLastQuestion
        ? this.$t(translations.seeOffer)
        : this.$t(translations.continue)
    },
    imgProps() {
      return { height: 24, width: 43, alt: '' }
    },
  },
  watch: {
    funnel: {
      handler(newValue) {
        this.activeQuestions = this.getActiveQuestions(newValue)
        this.form = this.generateFormModel(this.activeQuestions)
        this.$emit('show-button', {
          label: this.buttonLabel,
          visible: this.isStepComplete,
          scrollBottom: this.activeQuestions.length !== 1,
        })
      },
      immediate: true,
    },
  },
  methods: {
    getInfoBlockIcon(id) {
      return INFOBLOCK_ICONS[id] || INFOBLOCK_ICONS.info
    },
    getOptionsDisplay(options) {
      const hasDescription = options.some((option) => option.description)

      if (!hasDescription) {
        return {
          optionsWrapper: 'flex -mx-1 flex-wrap',
          option: 'w-full md:w-1/2',
        }
      }

      return {
        optionsWrapper: 'flex -mx-1 flex-col',
        option: 'w-full',
      }
    },
    getActiveQuestions(funnel) {
      const { questions = [] } = funnel.find(({ step }) => step.active) || {}

      return questions.map((question) => {
        const infoList = question.subTitleList
        const hasOptions = !isEmpty(question.options)
        const hasInfoBlock = !isEmpty(question.infoBlock)

        return {
          ...question,
          // TODO https://backmarket.atlassian.net/browse/BROW-1272
          showInfo: !isEmpty(infoList),
          info: {
            text: infoList,
            tag: infoList.length > 1 ? 'ul' : 'p',
            subTag: infoList.length > 1 ? 'li' : 'span',
          },
          options: [
            ...(hasOptions
              ? question.options.map((option) => {
                  return {
                    ...option,
                    inputId: `${option.label}-${option.value}`,
                    icon: option?.icon?.light,
                  }
                })
              : []),
          ],
          hasInfoBlock,
          infoBlock: hasInfoBlock ? question.infoBlock : {},
          twClasses: hasOptions && this.getOptionsDisplay(question.options),
          isSelect: question.type === QUESTION_TYPES.SELECT,
          isRadio: question.type === QUESTION_TYPES.RADIO,
          isCheckbox: question.type === QUESTION_TYPES.CHECKBOX,
          onChange: (value) => this.handleChange({ key: question.key, value }),
        }
      })
    },
    generateFormModel(questions) {
      return questions.reduce((acc, question) => {
        const { key, type, options } = question

        const defaultValue = DEFAULTS_LOOKUP[type]

        const preSelected = options.find((option) => option.selected) || {}
        const value = preSelected.value || defaultValue

        return {
          ...acc,
          [key]: value,
        }
      }, {})
    },
    generateResetPayload(key) {
      const atIndex = this.activeQuestions.findIndex(
        (question) => question.key === key,
      )

      return Object.keys(this.form)
        .slice(atIndex + 1)
        .reduce((acc, keyToReset) => {
          return [...acc, keyToReset]
        }, [])
    },
    handleChange({ key }) {
      const resetUrlPayload = this.generateResetPayload(key)
      // To ensure smooth UX behaviour remove the outated questions as the user interact
      // before updating the url with the current state
      if (!isEmpty(resetUrlPayload)) {
        this.activeQuestions = this.activeQuestions.filter((question) => {
          return !resetUrlPayload.includes(question.key)
        })
      }

      const form = {
        ...this.form,
        nextStep: false,
      }

      this.$emit('next-question', {
        form,
        reset: resetUrlPayload,
        trackingKey: key,
      })
    },
    completeStep() {
      const { key: trackingKey } =
        this.activeQuestions[(this, this.activeQuestions.length - 1)]

      if (this.isLastQuestion) {
        this.$emit('submit-answers', {
          form: this.form,
          activeStep: this.activeStep.name,
          trackingKey,
        })

        return
      }

      const form = {
        ...this.form,
        nextStep: true,
      }

      this.$emit('next-step', {
        scrollToTop: true,
        form,
        activeStep: this.activeStep.name,
        trackingKey,
      })
    },
  },
}
